import { useState } from "react";
import { useLocation, useParams } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useGetSession, useAnswerAgentQuestion, getGetSessionQueryKey } from "@workspace/api-client-react";
import type { QuestionItem, Session } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/useAuth";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, ArrowRight, Sparkles, Pencil, Check, ChevronLeft } from "lucide-react";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { useTrialExhaustedDialog } from "@/hooks/use-trial-exhausted-dialog";
import { Waveform } from "@/components/waveform";
import { FreeResponseComposer } from "@/components/reflect/free-response-composer";
import { cn } from "@/lib/utils";

type Step = "answer" | "addmore" | "review";

export default function ReflectScreen() {
  const params = useParams();
  const [, setLocation] = useLocation();
  const sessionId = params.sessionId!;

  const { data: session, isLoading } = useGetSession(sessionId);

  const isMultiQuestion = !!(session?.questions && session.questions.length > 0);

  return isMultiQuestion
    ? <MultiQuestionFlow sessionId={sessionId} session={session!} />
    : <SingleQuestionFlow sessionId={sessionId} session={session} isLoading={isLoading} />;
}

/* ── Multi-question flow for template-based trigger sessions ── */
function MultiQuestionFlow({
  sessionId,
  session,
}: {
  sessionId: string;
  session: Session;
}) {
  const [, setLocation] = useLocation();
  const { refetch } = useAuth();
  const { trialExhaustedDialog, showTrialExhaustedDialog } = useTrialExhaustedDialog();
  const questions = (session.questions ?? []) as QuestionItem[];
  const total = questions.length;

  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState<string[]>(Array(total).fill(""));
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [showReview, setShowReview] = useState(false);
  const [returnToReview, setReturnToReview] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const { mutate: answerQuestion, isPending } = useAnswerAgentQuestion({
    mutation: {
      onSuccess: (data) => {
        void refetch();
        setLocation(`/journal/${data.id}`);
      },
      onError: (err: unknown) => {
        if (showTrialExhaustedDialog(err)) {
          setSubmitError("");
          void refetch();
          return;
        }
        setSubmitError("Could not create your journal. Please try again.");
      },
    },
  });

  const currentQ = questions[qIndex];
  const CARD_COLORS = ["card-blush", "card-lavender", "card-sage", "card-golden", "card-sky", "card-terra", "card-blush", "card-lavender"];

  // Dictation lives inside FreeResponseComposer, which is keyed per question —
  // changing questions unmounts it and stops the mic.
  const goToQuestion = (i: number, fromReview: boolean) => {
    setQIndex(i);
    setCurrentAnswer(answers[i] ?? "");
    setReturnToReview(fromReview);
  };

  const handleNext = (ans?: string) => {
    const finalAns = (ans ?? currentAnswer).trim();
    if (!finalAns) return;
    const newAnswers = [...answers];
    newAnswers[qIndex] = finalAns;
    setAnswers(newAnswers);

    if (returnToReview) {
      setReturnToReview(false);
      setCurrentAnswer("");
      setShowReview(true);
    } else if (qIndex < total - 1) {
      goToQuestion(qIndex + 1, false);
    } else {
      setCurrentAnswer("");
      setShowReview(true);
    }
  };

  const handleBack = () => {
    if (returnToReview) {
      setReturnToReview(false);
      setCurrentAnswer("");
      setShowReview(true);
      return;
    }
    if (qIndex > 0) {
      goToQuestion(qIndex - 1, false);
    }
  };

  const handleGenerate = () => {
    if (isPending) return;
    setSubmitError("");
    const questionsAndAnswers = questions.map((q, i) => ({
      question: q.question,
      answer: answers[i] ?? "",
    }));
    answerQuestion({ id: sessionId, data: { questionsAndAnswers } });
  };

  if (showReview) {
    return (
      <>
        {trialExhaustedDialog}
      <div className="min-h-screen flex flex-col relative overflow-hidden bg-background">
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-lavender/40 blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-blush/30 blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none" />
        <div className="flex-1 flex flex-col items-center justify-start px-5 pt-10 pb-16 max-w-xl mx-auto w-full relative z-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full flex flex-col">
            <div className="flex flex-col items-center text-center gap-2 mb-7">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sage to-sky flex items-center justify-center">
                <Check className="w-5 h-5 text-white" />
              </div>
              <h2 className="font-display text-2xl font-medium text-foreground">Your reflection session</h2>
              <p className="text-sm text-muted-foreground">Here's everything you shared. Ready to generate your journal?</p>
            </div>

            <div className="space-y-3 mb-7 max-h-[55vh] overflow-y-auto pr-1">
              {questions.map((q, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="glass rounded-2xl p-4 border border-white/60"
                >
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Q{i + 1} of {total}</p>
                  <p className="text-xs text-foreground/60 mb-2 leading-relaxed">"{q.question}"</p>
                  <div className={cn("rounded-xl px-3 py-2.5", CARD_COLORS[i % CARD_COLORS.length])}>
                    <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{answers[i]}</p>
                  </div>
                  <button
                    onClick={() => { setShowReview(false); goToQuestion(i, true); }}
                    className="text-[10px] text-primary hover:underline mt-1.5 flex items-center gap-1"
                  >
                    <Pencil className="w-2.5 h-2.5" /> Edit
                  </button>
                </motion.div>
              ))}
            </div>

            {submitError && (
              <p className="text-sm text-center text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3">
                {submitError}
              </p>
            )}

            <button
              onClick={handleGenerate}
              disabled={isPending}
              className="w-full flex items-center justify-center gap-2 py-4 rounded-full bg-primary text-white font-semibold text-base hover:bg-primary/90 transition-all shadow-sm disabled:opacity-50"
            >
              {isPending ? (
                <><div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> Creating your Unfolding…</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Generate my Unfolding</>
              )}
            </button>
            <button
              onClick={() => { setShowReview(false); goToQuestion(total - 1, false); }}
              disabled={isPending}
              className="text-sm text-muted-foreground hover:text-foreground text-center transition-colors mt-3"
            >
              ← Go back to questions
            </button>
          </motion.div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      {trialExhaustedDialog}
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-background">
      <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-lavender/40 blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-blush/30 blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none" />

      <div className="flex-1 flex flex-col items-center justify-start px-5 pt-10 pb-12 max-w-xl mx-auto w-full relative z-10">
        {/* Progress bar + counter */}
        <div className="w-full mb-8">
          <div className="flex items-center justify-between mb-2">
            <button onClick={handleBack} disabled={qIndex === 0 && !returnToReview} className="text-xs text-muted-foreground disabled:opacity-30 hover:text-foreground transition-colors flex items-center gap-1">
              <ChevronLeft className="w-3.5 h-3.5" /> {returnToReview ? "Back to review" : "Back"}
            </button>
            <span className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
              {returnToReview ? `Editing Q ${qIndex + 1} of ${total}` : `Q ${qIndex + 1} of ${total}`}
            </span>
            <span className="text-xs text-muted-foreground/50">{session.sourceTitle?.slice(0, 20)}</span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-primary rounded-full"
              animate={{ width: `${(answers.filter(Boolean).length / total) * 100}%` }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={qIndex}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            className="w-full flex flex-col items-center"
          >
            {/* Badge */}
            <div className="mb-6 flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-lavender to-blush flex items-center justify-center shadow-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
            </div>

            {/* Question */}
            <h2 className="font-display text-xl md:text-2xl font-medium text-center leading-tight text-foreground mb-7">
              {currentQ?.question}
            </h2>

            <FreeResponseComposer
              value={currentAnswer}
              onChange={setCurrentAnswer}
              suggestions={currentQ?.quickReplies}
              continueLabel={returnToReview ? "Save changes" : qIndex === total - 1 ? "Review answers" : "Continue"}
              onContinue={handleNext}
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
    </>
  );
}

/* ── Single-question flow (resource-based trigger sessions) ── */
function SingleQuestionFlow({
  sessionId,
  session,
  isLoading,
}: {
  sessionId: string;
  session: Session | undefined;
  isLoading: boolean;
}) {
  const [, setLocation] = useLocation();
  const { refetch } = useAuth();
  const { trialExhaustedDialog, showTrialExhaustedDialog } = useTrialExhaustedDialog();
  const [step, setStep] = useState<Step>("answer");
  const [answer, setAnswer] = useState("");
  const [extraNote, setExtraNote] = useState("");
  const [returnToReview, setReturnToReview] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const queryClient = useQueryClient();

  const { mutate: answerQuestion, isPending } = useAnswerAgentQuestion({
    mutation: {
      onSuccess: (data) => {
        void refetch();
        // The backend marks the session complete before responding. Mirror that in the
        // cache so /processing doesn't read the stale "needs_answer" entry on its first
        // render and bounce the user back to /reflect.
        queryClient.setQueryData<Session>(getGetSessionQueryKey(sessionId), (prev) =>
          prev ? { ...prev, status: "complete", journalId: data.id } : prev,
        );
        setLocation(`/processing/${sessionId}?journalId=${data.id}`);
      },
      onError: (err: unknown) => {
        if (showTrialExhaustedDialog(err)) {
          setSubmitError("");
          void refetch();
          return;
        }
        setSubmitError("Could not create your journal. Please try again.");
      },
    },
  });

  const voice2 = useVoiceInput({
    onTranscript: (text) => setExtraNote((prev) => (prev ? prev + " " + text : text)),
  });

  const editAnswer = (fromReview: boolean) => {
    setReturnToReview(fromReview);
    setStep("answer");
  };

  // Dictation for the answer step lives in FreeResponseComposer, which unmounts
  // (and stops the mic) when the step changes.
  const handleAnswerContinue = (finalAnswer: string) => {
    if (!finalAnswer.trim()) return;
    setAnswer(finalAnswer);
    setStep(returnToReview ? "review" : "addmore");
    setReturnToReview(false);
  };

  const handleGenerate = () => {
    const combined = extraNote.trim() ? `${answer.trim()}\n\n${extraNote.trim()}` : answer.trim();
    if (!combined || isPending) return;
    if (voice2.isListening) voice2.stop();
    setSubmitError("");
    answerQuestion({ id: sessionId, data: { answer: combined } });
  };

  if (isLoading || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const question = session.agentQuestion || "What resonated most deeply with you?";

  return (
    <>
      {trialExhaustedDialog}
    <div className="min-h-screen flex flex-col relative overflow-hidden bg-background">
      <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-lavender/40 blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-blush/30 blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none" />

      <div className="flex-1 flex flex-col items-center justify-start px-5 pt-12 pb-12 max-w-xl mx-auto w-full relative z-10">
        {/* Progress dots */}
        <div className="flex items-center gap-2 mb-10 self-center">
          {(["answer", "addmore", "review"] as Step[]).map((s, i) => (
            <div
              key={s}
              className={cn(
                "rounded-full transition-all duration-300",
                step === s ? "w-6 h-2 bg-primary" : i < (["answer", "addmore", "review"] as Step[]).indexOf(step) ? "w-2 h-2 bg-primary/50" : "w-2 h-2 bg-muted",
              )}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {step === "answer" && (
            <motion.div key="answer" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="w-full flex flex-col items-center">
              <div className="mb-7 flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-lavender to-blush flex items-center justify-center shadow-lg">
                  <Sparkles className="w-6 h-6 text-lavender-foreground" />
                </div>
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">One question for you</span>
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-medium text-center leading-tight text-foreground mb-8">{question}</h2>
              <FreeResponseComposer
                value={answer}
                onChange={setAnswer}
                suggestions={session.quickReplies}
                continueLabel={returnToReview ? "Save changes" : "Continue"}
                onContinue={handleAnswerContinue}
              />
            </motion.div>
          )}

          {step === "addmore" && (
            <motion.div key="addmore" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="w-full flex flex-col">
              <div className="card-lavender rounded-3xl p-5 mb-6 relative border border-white/60">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0"><p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Your answer</p><p className="text-base text-foreground leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{answer}</p></div>
                  <button onClick={() => editAnswer(false)} className="shrink-0 w-8 h-8 rounded-full bg-white/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" title="Edit answer"><Pencil className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <h2 className="font-display text-xl md:text-2xl font-medium text-foreground mb-2">Anything else you'd like to add?</h2>
              <p className="text-sm text-muted-foreground mb-6">This is completely optional. Skip it if you're ready.</p>
              <div className="glass rounded-3xl p-1 shadow-sm mb-5">
                <div className="flex items-center justify-between px-5 pt-4 pb-2">
                  <span className="text-xs text-muted-foreground font-medium">Optional notes</span>
                  <button onClick={() => voice2.toggle()} className={cn("w-7 h-7 rounded-lg flex items-center justify-center transition-all", voice2.isListening ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary")}>
                    {voice2.isListening ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                  </button>
                </div>
                {voice2.isListening && <div className="flex justify-center pb-2"><Waveform active className="h-6" /></div>}
                <textarea value={extraNote} onChange={(e) => setExtraNote(e.target.value)} placeholder="Any extra context, feelings, or details…" className="w-full min-h-[100px] bg-transparent px-5 pb-4 text-base text-foreground placeholder:text-muted-foreground/50 focus:outline-none resize-none" />
              </div>
              <div className="flex flex-col gap-3">
                <button onClick={() => setStep("review")} className="flex items-center justify-center gap-2 py-3.5 rounded-full bg-primary text-white font-medium hover:bg-primary/90 transition-all shadow-sm">
                  Review my response <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => { setExtraNote(""); setStep("review"); }} className="text-sm text-muted-foreground hover:text-foreground text-center transition-colors">
                  Skip — go straight to review
                </button>
              </div>
            </motion.div>
          )}

          {step === "review" && (
            <motion.div key="review" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="w-full flex flex-col">
              <div className="mb-6 flex flex-col items-center gap-2 text-center">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sage to-sky flex items-center justify-center">
                  <Check className="w-5 h-5 text-sage-foreground" />
                </div>
                <h2 className="font-display text-2xl font-medium text-foreground">Here's what you shared</h2>
                <p className="text-sm text-muted-foreground">Review your response below. If it looks good, generate your Unfolding.</p>
              </div>
              <div className="mb-3">
                <div className="flex items-center justify-between px-1 mb-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Your answer</p>
                  <button onClick={() => editAnswer(true)} className="text-xs text-primary hover:underline flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                </div>
                <div className="card-lavender rounded-2xl px-5 py-4 border border-white/60">
                  <p className="text-base text-foreground leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{answer}</p>
                </div>
              </div>
              {extraNote.trim() && (
                <div className="mb-5">
                  <div className="flex items-center justify-between px-1 mb-2">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Extra notes</p>
                    <button onClick={() => setStep("addmore")} className="text-xs text-primary hover:underline flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                  </div>
                  <div className="card-sage rounded-2xl px-5 py-4 border border-white/60">
                    <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{extraNote}</p>
                  </div>
                </div>
              )}
              <div className="mt-2 flex flex-col gap-3">
                {submitError && (
                  <p className="text-sm text-center text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                    {submitError}
                  </p>
                )}
                <button onClick={handleGenerate} disabled={isPending} className="flex items-center justify-center gap-2 py-4 rounded-full bg-primary text-white font-semibold text-base hover:bg-primary/90 transition-all shadow-sm disabled:opacity-50">
                  {isPending ? (<><div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />Creating your Unfolding…</>) : (<><Sparkles className="w-4 h-4" />Generate my Unfolding</>)}
                </button>
                <button onClick={() => setStep("addmore")} disabled={isPending} className="text-sm text-muted-foreground hover:text-foreground text-center transition-colors">
                  ← Go back and add more
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
    </>
  );
}
